import React, { FunctionComponent, useState } from 'react';
import { CheckupCard } from '../CheckupCard';
import { Section } from '../Section';
import { Button, Heading, Text } from '@companion-professional/components';
import { AddCheckupToSurveyWizard } from '../CheckupCard/AddCheckupToSurveyWizard';

interface SurveyCheckupCardsSectionProps {
  surveyId: string;
  isNewSurvey: boolean;

  // preSelectedCheckupIds is a list of checkup request ids that are either already attached to the survey (when
  // editing an existing survey) or were selected by the user to be attached to the survey (like when creating a new
  // survey for from a checkup of the bulk selection)
  preSelectedCheckupIds: string[];

  // toBeAttachedCheckupIds is a list of checkup request ids that are to be attached to the survey.
  toBeAttachedCheckupIds: string[];

  // unattachedCheckupIds is a list of checkup request ids that are not currently attached to any survey.
  unattachedCheckupIds: string[];

  // setCheckupsToAddIds is a function that sets the checkup request ids that will be attached to the survey (when the
  // user saves the survey).  Essentially, this is a function that allows the parent component to manage the list of
  // checkup request ids that will be attached to the survey (i.e. the toBeAttachedCheckupIds list).
  setCheckupsToAddIds: React.Dispatch<React.SetStateAction<string[]>>;

  // checkupPagination is the number of checkup cards to display (i.e. load) at a time.  This is also the number of
  // additional checkup cards to load when the user clicks the 'Load More Checkups' button. Default is 3.
  checkupPagination?: number;
}

// SurveyCheckupCardsSection displays video cards for checkups that are currently attached to a survey, the ones that
// are selected to be attached to the survey, and ones that are not attached to any survey.  Because we were running
// into rate limiting issues with Mux, we decided to paginate the loading of the checkup cards.  More checkup cards can
// be loaded by clicking the 'Load More Checkups' button.
export const SurveyCheckupCardsSection: FunctionComponent<SurveyCheckupCardsSectionProps> = ({
  surveyId,
  isNewSurvey,
  preSelectedCheckupIds,
  toBeAttachedCheckupIds,
  unattachedCheckupIds,
  setCheckupsToAddIds,
  checkupPagination = 3
}) => {
  const [maxNumCheckupCards, setMaxNumCheckupCards] = useState(checkupPagination);

  const numberOfCheckupsShown = Math.min(
    maxNumCheckupCards,
    preSelectedCheckupIds.length + unattachedCheckupIds.length
  );

  const totalNumberOfCheckups = preSelectedCheckupIds.length + unattachedCheckupIds.length;

  return (
    <Section className="py-4">
      <Heading level="section">Visual Exam Insights</Heading>
      <Text tone="muted" className="mb-4">
        Checkups representing physical exam findings.
      </Text>

      <div className="grid grid-cols-3 gap-2">
        {preSelectedCheckupIds.map((cid) => (
          <CheckupCard
            key={`checkup-card-${cid}-${surveyId}`}
            checkupRequestId={cid}
            cardState={isNewSurvey ? 'to_be_attached' : 'attached'}
          />
        ))}
        {unattachedCheckupIds.map((unattachedCheckupId, i) =>
          i + 1 + preSelectedCheckupIds.length > maxNumCheckupCards ? null : (
            <CheckupCard
              key={`checkup-to-add-card-${unattachedCheckupId}-${surveyId}`}
              checkupRequestId={unattachedCheckupId}
              cardState={toBeAttachedCheckupIds.includes(unattachedCheckupId) ? 'to_be_attached' : 'unattached'}
            >
              {toBeAttachedCheckupIds.includes(unattachedCheckupId) ? (
                <Button
                  onClick={() => {
                    // Remove the checkup from the list of checkups to be attached/added to the survey
                    setCheckupsToAddIds((prevAddIds) =>
                      prevAddIds.filter((checkupId) => checkupId !== unattachedCheckupId)
                    );
                  }}
                  size="small"
                  variant="destructive"
                >
                  Cancel Attach
                </Button>
              ) : (
                <AddCheckupToSurveyWizard
                  onAllAnswersYes={() => setCheckupsToAddIds((ids) => [...ids, unattachedCheckupId])}
                />
              )}
            </CheckupCard>
          )
        )}
      </div>
      {/* Don't show the pagination stuff when we don't need to paginate */}
      {totalNumberOfCheckups <= checkupPagination ? null : (
        <div className="flex flex-col items-center gap-2 pt-4">
          <Text size="sm" tone="muted">
            Showing{' '}
            {numberOfCheckupsShown === totalNumberOfCheckups
              ? 'all checkups'
              : `${numberOfCheckupsShown} of ${totalNumberOfCheckups} checkup${totalNumberOfCheckups > 1 ? 's' : ''}`}
          </Text>
          {numberOfCheckupsShown < totalNumberOfCheckups ? (
            <Button
              onClick={() => {
                setMaxNumCheckupCards((num) => num + 3);
              }}
              size="small"
              variant="primary"
            >
              Load More Checkups
            </Button>
          ) : null}
        </div>
      )}
    </Section>
  );
};
